import { createFileRoute } from "@tanstack/react-router";
import { MagistralPage } from "@/components/pages/MagistralPage";
import { magistralWebpSrcSet, magistralWebpUrl } from "@/assets/magistral";
import { FAQ, MAGISTRAL_SEO } from "@/content/magistral";
import { breadcrumbLd, faqLd, serviceLd, webPageLd } from "@/lib/seo-jsonld";

const SITE = "https://kentro.com.br";

const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Kentro Sistemas Inteligentes",
  alternateName: "Kentro",
  url: SITE,
  logo: {
    "@type": "ImageObject",
    url: `${SITE}/og/og-default.png`,
    width: 1200,
    height: 630,
  },
  description:
    "Plataforma de Chat omnichannel, CRM e IA para atendimento no WhatsApp oficial, Instagram, voz, e-mail e webchat.",
  areaServed: "BR",
  address: {
    "@type": "PostalAddress",
    addressCountry: "BR",
    addressRegion: "MG",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      telephone: "+55-38-3220-8706",
      email: "comercial@kentro.com.br",
      availableLanguage: ["pt-BR"],
      areaServed: "BR",
    },
  ],
  sameAs: [
    "https://www.instagram.com/kentrosistemas",
    "https://www.linkedin.com/company/kentrosistemas",
  ],
};

const softwareLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Kentro para Farmácias de Manipulação",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, Android, iOS",
  url: MAGISTRAL_SEO.url,
  description: MAGISTRAL_SEO.description,
  inLanguage: "pt-BR",
  image: MAGISTRAL_SEO.ogImage,
  featureList: [
    "WhatsApp oficial multiatendente",
    "Funil de orçamentos de fórmulas",
    "Recompra de uso contínuo",
    "Integração com Fórmula Certa",
    "Aplicativo de acompanhamento",
    "Dashboards e ranking de atendimento",
  ],
  offers: { "@type": "Offer", priceCurrency: "BRL", price: "0", description: "Diagnóstico gratuito" },
  publisher: { "@id": `${SITE}/#organization` },
};

const magistralServiceLd = serviceLd({
  url: MAGISTRAL_SEO.url,
  name: "Kentro para farmácias de manipulação",
  description: MAGISTRAL_SEO.description,
  serviceType: "Atendimento omnichannel, CRM e IA para farmácias magistrais",
});

const magistralWebPageLd = {
  ...webPageLd({
    url: MAGISTRAL_SEO.url,
    name: MAGISTRAL_SEO.title,
    description: MAGISTRAL_SEO.description,
    primaryImage: MAGISTRAL_SEO.ogImage,
  }),
  about: { "@id": `${SITE}/#organization` },
  audience: {
    "@type": "BusinessAudience",
    name: "Farmácias de manipulação e farmácias magistrais",
  },
};

const HERO_WEBP = magistralWebpUrl("hero-vitalis");
const HERO_SRCSET = magistralWebpSrcSet("hero-vitalis");

const magistralBreadcrumbLd = breadcrumbLd([
  { name: "Início", path: "/" },
  { name: "Farmácias de manipulação", path: "/magistral" },
]);

export const Route = createFileRoute("/magistral")({
  head: () => ({
    meta: [
      { title: MAGISTRAL_SEO.title },
      { name: "description", content: MAGISTRAL_SEO.description },
      {
        name: "keywords",
        content:
          "software para farmácia de manipulação, CRM farmácia magistral, WhatsApp oficial farmácia, integração Fórmula Certa, recompra uso contínuo, atendimento omnichannel farmácia",
      },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "author", content: "Kentro Sistemas Inteligentes" },
      { property: "og:site_name", content: "Kentro" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:title", content: MAGISTRAL_SEO.title },
      { property: "og:description", content: MAGISTRAL_SEO.description },
      { property: "og:url", content: MAGISTRAL_SEO.url },
      { property: "og:type", content: "website" },
      { property: "og:image", content: MAGISTRAL_SEO.ogImage },
      { property: "og:image:alt", content: "Kentro para farmácias de manipulação" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: MAGISTRAL_SEO.title },
      { name: "twitter:description", content: MAGISTRAL_SEO.description },
      { name: "twitter:image", content: MAGISTRAL_SEO.ogImage },
    ],
    links: [
      { rel: "canonical", href: MAGISTRAL_SEO.url },
      // LCP do hero: preload da imagem WebP com as mesmas regras de srcset/sizes do componente.
      ...(HERO_WEBP
        ? [
            {
              rel: "preload",
              as: "image",
              href: HERO_WEBP,
              imagesrcset: HERO_SRCSET,
              imagesizes: "(max-width: 640px) 300px, (max-width: 1024px) 45vw, 360px",
              fetchpriority: "high",
            },
          ]
        : []),
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(organizationLd) },
      { type: "application/ld+json", children: JSON.stringify(magistralWebPageLd) },
      { type: "application/ld+json", children: JSON.stringify(magistralBreadcrumbLd) },
      { type: "application/ld+json", children: JSON.stringify(magistralServiceLd) },
      { type: "application/ld+json", children: JSON.stringify(softwareLd) },
      ...(FAQ.length
        ? [{ type: "application/ld+json", children: JSON.stringify(faqLd(FAQ)) }]
        : []),
    ],
  }),
  component: MagistralRouteComponent,
});

/**
 * Mesmo stack de rastreio do site principal: Meta Pixel condicionado ao
 * consentimento de marketing (LGPD); GA4/GTM já vêm do layout raiz.
 */
function MagistralRouteComponent() {
  return (
    <>
      <MagistralPage />
    </>
  );
}
